// N8 voice preference — persisted in localStorage. Every read-aloud
// surface in the app (Affirmations, Today's Intention, Quiet Reads,
// Visualizations, N8 Wisdom) checks this before it speaks so a single
// setting on the Profile page governs the whole experience.
//
// Shape: { enabled: boolean, gender: "female" | "male" }
//
// The default is "female · on" because most members expect N8 to sound
// soft and welcoming out of the box — they can switch on Profile.

const KEY = "n8_voice_pref_v1";

const DEFAULT = Object.freeze({ enabled: true, gender: "female" });

/** Read the current preference, always returning a valid object. */
export function getVoicePref() {
  if (typeof window === "undefined") return { ...DEFAULT };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed?.enabled !== false,
      gender: parsed?.gender === "male" ? "male" : "female",
    };
  } catch {
    return { ...DEFAULT };
  }
}

/**
 * Persist an update to the preference. Partial updates are supported —
 * pass only the fields you want to change.
 * Emits a "n8-voice-pref-changed" window event so any listening surface
 * can react immediately (e.g. stop the current utterance if disabled).
 */
export function setVoicePref(partial) {
  if (typeof window === "undefined") return getVoicePref();
  const next = { ...getVoicePref(), ...partial };
  next.enabled = next.enabled !== false;
  next.gender = next.gender === "male" ? "male" : "female";
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("n8-voice-pref-changed", { detail: next }));
  } catch { /* noop */ }
  return next;
}

/** Convenience: is N8's voice currently enabled? */
export function isVoiceEnabled() {
  return getVoicePref().enabled;
}
